
import React, { useState } from "react";
import { z } from "zod";
import { 
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";
import { UseFormReturn } from "react-hook-form";

// Health section schema (subset of program form schema)
export const healthInfoSchema = z.object({
  medicalConditions: z.enum(["yes", "no"], {
    required_error: "Please select yes or no"
  }),
  medicalDetails: z.string()
    .min(1, "Please provide details about the medical conditions")
    .optional()
    .refine((val) => {
      // If no value is provided but should be required based on medicalConditions
      if (!val) return true;
      return val.trim().length > 0;
    }, "Please provide details about the medical conditions"),
  injuries: z.string().optional(),
});

export type HealthInfoFormValues = z.infer<typeof healthInfoSchema>;

interface HealthInformationSectionProps {
  form: UseFormReturn<any>;
}

export default function HealthInformationSection({ form }: HealthInformationSectionProps) {
  const [showHealthSection, setShowHealthSection] = useState(true);
  
  const medicalConditions = form.watch("medicalConditions");
  const showMedicalDetails = medicalConditions === "yes";
  
  // Set up conditional validation for medicalDetails
  React.useEffect(() => {
    if (showMedicalDetails) {
      form.register("medicalDetails", { 
        required: "Please provide details about the medical conditions" 
      });
    } else {
      form.unregister("medicalDetails");
    }
  }, [form, showMedicalDetails]);
  
  return (
    <div className="space-y-6 p-6 border border-white/20 rounded-lg bg-white/5 mt-8">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-white font-medium text-lg mb-2">Health & Safety Information</h3>
          <p className="text-white/70 text-sm mb-4">
            This information helps ensure the program is safe and appropriate for the client's health status
          </p>
        </div>
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={(e) => {
            e.preventDefault(); // Prevent form submission
            setShowHealthSection(!showHealthSection);
          }}
          className="text-white hover:bg-white/10"
        >
          {showHealthSection ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </Button>
      </div>
      
      <div className={`space-y-6 ${showHealthSection ? 'block' : 'hidden'}`}>
        <FormField
          control={form.control}
          name="medicalConditions"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-white text-base">
                Medical Conditions <span className="text-red-500">*</span>
              </FormLabel>
              <div className="flex flex-col space-y-2">
                <p className="text-white/70 text-sm">
                  Does the client have any existing medical conditions (e.g., heart disease, diabetes, asthma) that we should know about?
                </p>
                <div className="flex gap-4 mt-1">
                  <label className="flex items-center gap-2 text-white">
                    <input 
                      type="radio" 
                      name="medicalConditions" 
                      value="yes"
                      checked={field.value === "yes"}
                      onChange={() => field.onChange("yes")}
                      className="h-4 w-4" 
                    />
                    <span>Yes</span>
                  </label>
                  <label className="flex items-center gap-2 text-white">
                    <input 
                      type="radio" 
                      name="medicalConditions" 
                      value="no"
                      checked={field.value === "no"}
                      onChange={() => field.onChange("no")}
                      className="h-4 w-4" 
                    />
                    <span>No</span>
                  </label>
                </div>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        {showMedicalDetails && (
          <FormField
            control={form.control}
            name="medicalDetails"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white text-sm">
                  Please specify condition(s) and any medications <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="List all medical conditions and current medications"
                    className="bg-white/20 border-white/30 text-white min-h-[100px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="injuries"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-white text-base">
                Injuries & Chronic Pain <span className="text-white/60">(Optional)</span>
              </FormLabel>
              <div className="flex flex-col">
                <p className="text-white/70 text-sm mb-2">
                  Does the client have any past or current injuries or chronic pain (e.g., back, knees, shoulders) that could affect exercise?
                </p>
                <FormControl>
                  <Textarea
                    placeholder="Please list any injuries or areas of chronic pain that might affect exercise performance"
                    className="bg-white/20 border-white/30 text-white min-h-[100px]"
                    {...field}
                  />
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
